import { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTripDetail, useDeclineTrip, useApproveQuote, useResetTrip, useMe, useManualQuote, usePostNow, useSendUpdatedQuote } from '../lib/api';
import { getMruReasons, pushMruReason } from '../lib/mru-reasons';
import StatusBadge from '../components/StatusBadge.jsx';
import QuoteIdLink from '../components/QuoteIdLink.jsx';
import VerdictCard from '../components/TrsVerdictCard.jsx';
import { CR_PHASE_LABELS } from '../lib/cr-phase.js';

// Format an Avinode price captured on a requested aircraft (aircraft_json
// sellerPrice/requestedPrice). USD renders as $; other currencies show the code.
function fmtAvinodePrice(amount, currency) {
  if (amount == null) return null;
  const n = Number(amount).toLocaleString();
  return (currency && currency !== 'USD') ? `${n} ${currency}` : `$${n}`;
}

function Section({ title, children, action }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-base">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// Human labels for phase2_decision enum values. Kept in sync with
// sentinel-form-filler.js wiring + migrations/019-phase2-decision.sql.
const PHASE2_DECISION_LABELS = {
  quotes_added:                       { tone: 'green', label: 'Quotes added',           icon: '✓' },
  no_flight_options:                  { tone: 'red',   label: 'No flight options',      icon: '✗', sub: 'Sentinel returned no flight options' },
  no_matching_fleet_group:            { tone: 'red',   label: 'No matching fleet group', icon: '✗', sub: 'Options returned but none matched requested fleet group' },
  no_matching_fleet_group_mixed:      { tone: 'red',   label: 'No matching fleet group (mixed fleet)', icon: '✗' },
  under_min_flight_time:              { tone: 'red',   label: 'Below min flight time',   icon: '✗', sub: 'Mid/Heavy quotes declined for sub-minimum per-leg time' },
  all_substituted_tails:              { tone: 'gray',  label: 'All substituted tails',   icon: '⊘', sub: 'All requested tails are TAIL_SUBSTITUTIONS — Avinode left open' },
  mixed_fleet_no_quotes:              { tone: 'red',   label: 'Mixed fleet, no quotes',  icon: '✗' },
  excluded_domestic_airport:          { tone: 'red',   label: 'Excluded domestic airport', icon: '✗' },
  light_jet_excluded_airport:         { tone: 'red',   label: 'Light Jet excluded airport', icon: '✗' },
  no_international_operator:          { tone: 'red',   label: 'No international (operator policy)', icon: '✗' },
  asap_international_not_approved:    { tone: 'red',   label: 'ASAP international not approved', icon: '✗' },
  permit_lead_time_insufficient:      { tone: 'red',   label: 'Permit lead time insufficient', icon: '✗' },
  excluded_international_destination: { tone: 'red',   label: 'Excluded international destination', icon: '✗' },
  airport_not_found:                  { tone: 'amber', label: 'Airport not found',      icon: '⚠' },
  tail_paused:                        { tone: 'gray',  label: 'Tail paused',            icon: '⊘' },
  fleet_group_paused:                 { tone: 'gray',  label: 'Fleet group paused',     icon: '⊘' },
  not_our_fleet:                      { tone: 'gray',  label: 'Not our fleet',           icon: '⊘', sub: 'Requested tails are not in this operator’s fleet — silently skipped, not quoted' },
};

function Phase2DecisionPanel({ decision, note, operatorReason, crPhase, crReason, sendBanner }) {
  if (!decision && !operatorReason && !crReason && !sendBanner) return null;
  const meta = PHASE2_DECISION_LABELS[decision] || { tone: 'gray', label: decision || 'Unknown', icon: '?' };
  const toneClass = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    red:   'bg-rose-50 border-rose-200 text-rose-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    gray:  'bg-slate-50 border-slate-200 text-slate-900',
  }[meta.tone] || 'bg-slate-50 border-slate-200 text-slate-900';

  return (
    <div className="card p-4 space-y-3">
      <h3 className="font-semibold">Why this trip ended where it did</h3>

      {/* TRS effective-verdict banner — a reviewer Declined the crew-solver HOLD with a
          corrected_verdict of SEND, so the EFFECTIVE verdict is "send it". Callout only; the
          actual send is the Approve quote / Use-updated-quote levers in the Actions panel. */}
      {sendBanner && (
        <div className="p-3 border rounded bg-emerald-50 border-emerald-300 text-emerald-900">
          <div className="text-xs uppercase tracking-wide opacity-70">TRS effective verdict</div>
          <div className="font-semibold text-base mt-0.5">✅ Go — send quote</div>
          <div className="text-sm mt-1">
            A reviewer overrode the crew-solver HOLD → this trip should be <strong>sent</strong>
            {sendBanner.qid ? <> (Sentinel quote #{sendBanner.qid})</> : null}. Use <strong>Send in Sentinel</strong> or{' '}
            <strong>Use updated Sentinel quote</strong> in the Actions panel to send it.
            {sendBanner.reviewer ? <span className="opacity-70"> — corrected by {sendBanner.reviewer}</span> : null}
          </div>
          {sendBanner.note && <div className="text-sm mt-2 font-mono opacity-90">{sendBanner.note}</div>}
        </div>
      )}

      {decision && (
        <div className={`p-3 border rounded ${toneClass}`}>
          <div className="text-xs uppercase tracking-wide opacity-70">Phase 2 (Sentinel search) result</div>
          <div className="font-semibold text-base mt-0.5">{meta.icon} {meta.label}</div>
          {meta.sub && <div className="text-xs opacity-80 mt-0.5">{meta.sub}</div>}
          {note && <div className="text-sm mt-2 font-mono opacity-90">{note}</div>}
          <div className="text-[10px] opacity-50 mt-2">enum: {decision}</div>
        </div>
      )}

      {operatorReason && (
        <div className="p-3 border rounded bg-amber-50 border-amber-200 text-amber-900">
          <div className="text-xs uppercase tracking-wide opacity-70">Operator decline reason</div>
          <div className="text-sm mt-1">{operatorReason}</div>
        </div>
      )}

      {crReason && (
        <div className="p-3 border rounded bg-rose-50 border-rose-200 text-rose-900">
          <div className="text-xs uppercase tracking-wide opacity-70">CR close reason{crPhase ? ` — ${CR_PHASE_LABELS[crPhase] || crPhase}` : ''}</div>
          <div className="text-sm mt-1 font-mono">{crReason}</div>
        </div>
      )}
    </div>
  );
}

// Source-aware metadata panel. Avinode trips already have the "SQP intent vs
// Avinode-confirmed state" section below, so we render this only for the
// non-Avinode sources (webhook/telegram/email) where the operator needs to
// know "who am I notifying when I decline this?" The reply path is different
// from Avinode's red-X click for each.
const SOURCE_META = {
  webhook: {
    label: 'Broker API (webhook)',
    tone: 'bg-tint-fill border-separator text-label',
    declineNote: 'Declining notifies the broker via webhook event (quote.declined). No Avinode action — this trip never touched Avinode.',
    fields: trip => [
      ['Broker', trip.broker_name || trip.broker_id || '—'],
      ['Broker email', trip.broker_email || '—'],
      ['Broker-API request ID', trip.webhook_request_id || trip.trip_id || '—'],
    ],
  },
  telegram: {
    label: 'Telegram (@SentinelQuoteBot)',
    tone: 'bg-sky-50 border-sky-200 text-sky-900',
    declineNote: 'Declining replies to the Telegram user. No Avinode action.',
    fields: trip => [
      ['Telegram user', trip.telegram_username ? `@${trip.telegram_username}` : (trip.telegram_user_id || '—')],
      ['Message ID', trip.telegram_message_id || '—'],
    ],
  },
  email: {
    label: 'Email (n8n bridge)',
    tone: 'bg-amber-50 border-amber-200 text-amber-900',
    declineNote: 'Declining sends a reply email to the customer. No Avinode action.',
    fields: trip => [
      ['Email message ID', trip.email_message_id || '—'],
      ['Customer email', trip.customer_email || '—'],
    ],
  },
};

function SourceInfoPanel({ trip }) {
  const meta = SOURCE_META[trip.source];
  if (!meta) return null;
  const fields = meta.fields(trip);
  return (
    <div className="card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Ingestion source</h3>
        <span className="text-xs text-slate-500">No Avinode counterpart</span>
      </div>
      <div className={`p-3 border rounded ${meta.tone}`}>
        <div className="text-xs uppercase tracking-wide opacity-70">{meta.label}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
          {fields.map(([k, v]) => (
            <div key={k}>
              <span className="opacity-70">{k}:</span>{' '}
              <span className="font-mono text-xs break-all">{v}</span>
            </div>
          ))}
        </div>
        <div className="text-xs italic mt-2 opacity-90">{meta.declineNote}</div>
      </div>
    </div>
  );
}

function StateColumn({ label, state }) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 mb-1">{label}</div>
      <ul className="text-sm space-y-0.5">
        {Object.entries(state).map(([k, v]) => (
          <li key={k}>
            <span className="text-slate-500">{k}:</span>{' '}
            <span className="font-mono text-xs">{
              v === null || v === undefined ? '—' :
              typeof v === 'boolean' ? (v ? '✓' : '✗') :
              String(v)
            }</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// PE-pricing-data Step 2/3 surface. Compact PE verdict cell: icon + recommended price.
function PeCell({ d }) {
  if (!d) return <span className="text-slate-300">—</span>;
  const tone = d.decision === 'reject' ? 'text-rose-600'
    : d.decision === 'adjust' ? 'text-amber-600' : 'text-emerald-600';
  const icon = d.decision === 'reject' ? '✗' : d.decision === 'adjust' ? '±' : '✓';
  return (
    <span className={tone} title={d.reason || ''}>
      {icon} {d.price != null ? `$${d.price.toLocaleString()}` : '—'}
    </span>
  );
}

// PE's recommended-price delta vs the sent price (challenger when running, else champion)
// — the "left money on the table?" signal.
function DeltaCell({ delta }) {
  if (delta == null || delta === 0) return <span className="text-slate-300">—</span>;
  const up = delta > 0;
  return (
    <span className={up ? 'text-amber-600' : 'text-sky-600'}>
      {up ? '+' : '−'}${Math.abs(delta).toLocaleString()}
    </span>
  );
}

function BookedCell({ booked }) {
  if (!booked) return <span className="text-slate-300">—</span>;
  if (!booked.booked) return <span className="text-slate-500" title={booked.status}>cancelled</span>;
  const t = booked.tier === 2 ? ' (T2)' : '';
  return <span className="text-emerald-700" title={`${booked.status}${booked.booked_date ? ' · ' + booked.booked_date : ''}`}>✓ booked{t}</span>;
}

// Full champion-vs-challenger reasoning, side by side, for one quote.
function PeReasonRow({ q }) {
  if (!q.pe || (!q.pe.champion && !q.pe.challenger)) return null;
  const Col = ({ label, d }) => (
    <div className="flex-1 min-w-0">
      <div className="text-xs uppercase text-slate-500 mb-0.5">{label}</div>
      {d ? (
        <div className="text-sm">
          <PeCell d={d} />
          {d.reason && <div className="text-xs text-slate-600 mt-0.5">{d.reason}</div>}
        </div>
      ) : <div className="text-sm text-slate-400">— not assessed</div>}
    </div>
  );
  return (
    <div className="border-b last:border-0 py-2">
      <div className="text-xs font-mono text-slate-500 mb-1">
        <QuoteIdLink id={q.sentinel_quote_id} url={q.sentinel_url} /> · {q.primary_aircraft_tail || '—'} · sent ${q.final_price?.toLocaleString() || '—'}
        {q.pe.delta != null && q.pe.delta !== 0 && <> · PE Δ <DeltaCell delta={q.pe.delta} /></>}
      </div>
      <div className="flex gap-4">
        <Col label="Champion (floor-check)" d={q.pe.champion} />
        <Col label="Challenger (contribution)" d={q.pe.challenger} />
      </div>
    </div>
  );
}

function ActionBar({ trip, quotes, onAction }) {
  const [decline, setDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineForce, setDeclineForce] = useState(false);

  const [approve, setApprove] = useState(false);
  const [approveQid, setApproveQid] = useState(quotes.find(q => !q.cleared_at)?.sentinel_quote_id || quotes[0]?.sentinel_quote_id || '');

  const [reset, setReset] = useState(false);
  const [resetForce, setResetForce] = useState(false);

  const [useQ, setUseQ] = useState(false);
  const [useQid, setUseQid] = useState('');
  const [usePrice, setUsePrice] = useState('');
  const [useTail, setUseTail] = useState('');

  const dDecline = useDeclineTrip();
  const dApprove = useApproveQuote();
  const dReset = useResetTrip();
  const dUseQuote = useManualQuote();
  const dPostNow = usePostNow();
  const [postNowMsg, setPostNowMsg] = useState(null);
  const liveQuotes = quotes.filter(q => !q.cleared_at);
  const isCr = trip.source === 'centralized_request';   // CR-Approve delivers the quote natively in Sentinel
  // "Use updated Sentinel quote" — send an operator-corrected re-quote (NEW Sentinel #) against an
  // existing held quote row (operatorQuoteId). Server clears the original + marks resolved.
  const dSendUpdated = useSendUpdatedQuote();
  const [updShow, setUpdShow] = useState(false);
  const [updQuoteRowId, setUpdQuoteRowId] = useState('');
  const [updNewQid, setUpdNewQid] = useState('');
  const [updMsg, setUpdMsg] = useState(null);
  const updQidValid = /^\d{1,8}$/.test(updNewQid.trim());   // server enforces the same numeric-# guard
  const { data: meData } = useMe();
  const username = meData?.user?.username;
  const mruReasons = decline ? getMruReasons(username) : [];

  // S98: every action carries the LOADED row's operator_id so the server never
  // falls back to most-recent-row resolution on a trip_id collision.
  async function doDecline() {
    await dDecline.mutateAsync({ tripId: trip.trip_id, reason: declineReason, force: declineForce, operator: trip.operator_id });
    pushMruReason(username, declineReason);
    setDecline(false); setDeclineReason(''); setDeclineForce(false);
    onAction();
  }
  async function doApprove() {
    await dApprove.mutateAsync({ tripId: trip.trip_id, sentinelQuoteId: approveQid, operator: trip.operator_id });
    setApprove(false);
    onAction();
  }
  async function doReset() {
    await dReset.mutateAsync({ tripId: trip.trip_id, force: resetForce, operator: trip.operator_id });
    setReset(false); setResetForce(false);
    onAction();
  }
  async function doUseQuote() {
    await dUseQuote.mutateAsync({ tripId: trip.trip_id, sentinelQuoteId: useQid.trim(), price: usePrice ? Number(usePrice) : undefined, tail: useTail.trim().toUpperCase(), operator: trip.operator_id });
    setUseQ(false); setUseQid(''); setUsePrice(''); setUseTail('');
    onAction();
  }
  // Send an operator-corrected re-quote (a NEW Sentinel quote #) against the original held quote.
  // The server resolves the GUID from the number, sends it, clears the original, and marks resolved.
  async function doSendUpdated() {
    setUpdMsg(null);
    try {
      await dSendUpdated.mutateAsync({ operatorQuoteId: updQuoteRowId, newSentinelQuoteId: updNewQid.trim() });
      setUpdMsg({ ok: true, text: `Updated quote #${updNewQid.trim()} sent in Sentinel ✓ — original held quote cleared + trip resolved. Refresh to see updated state.` });
      setUpdShow(false); setUpdQuoteRowId(''); setUpdNewQid('');
      onAction();
    } catch (e) {
      setUpdMsg({ ok: false, text: e?.message || 'Send failed' });
    }
  }
  // Post an already-approved quote to Avinode NOW (bypasses the operating-hours gate).
  // The server guard (assessPostNow) returns a clear reason if it isn't postable.
  async function doPostNow() {
    setPostNowMsg(null);
    try {
      await dPostNow.mutateAsync({ tripId: trip.trip_id, operator: trip.operator_id });
      setPostNowMsg({ ok: true, text: `Phase 4 triggered — posting ${trip.trip_id} to Avinode now. Refresh in ~1–2 min to confirm.` });
      onAction();
    } catch (e) {
      setPostNowMsg({ ok: false, text: e?.message || 'Post failed' });
    }
  }

  // CR-1078443 (2026-08-05): a TERMINAL trip must not offer Approve/Decline. Same predicate the
  // "Use updated Sentinel quote" button below already uses, and the same one manual-send.js:62
  // enforces server-side.
  //
  // Approve on a terminal trip is a silent no-op — sendViaCr's idempotency short-circuit returns
  // alreadySent without ever calling sendFn — so this is clarity, not safety.
  //
  // Decline is NOT a no-op and is the real reason this gate exists: actions.js declineTrip has no
  // is_approved/completed/cleared_at guard, so declining a DELIVERED CR trip calls
  // markTripResolved('declined') and flips it approved → declined. Nothing reaches the broker (the
  // outward red-X/webhook path is gated on source==='avinode'), but it writes a factually false
  // record on a trip the broker already holds a quote for.
  const isTerminal = !!(trip.completed || trip.do_not_post);
  const terminalTitle = isTerminal
    ? 'Trip is closed — reopen it with Reset if this needs to change'
    : undefined;

  return (
    <div className="card p-4 space-y-3">
      <h3 className="font-semibold">Actions</h3>
      <div className="flex flex-wrap gap-2">
        <button className="btn-success"  onClick={() => setApprove(true)} disabled={!quotes.length || isTerminal} title={terminalTitle}>✓ Approve quote</button>
        <button className="btn-danger"   onClick={() => setDecline(true)} disabled={isTerminal} title={terminalTitle}>✗ Decline trip</button>
        <button className="btn-secondary" onClick={() => setReset(true)}>↻ Reset</button>
        <button className="btn-secondary" onClick={() => setUseQ(true)}>＋ Use Sentinel quote</button>
        {(trip.source || 'avinode') === 'avinode' && (
          <button className="btn-secondary" onClick={doPostNow} disabled={dPostNow.isPending}
                  title="Post the approved quote to Avinode now, bypassing operating hours">
            {dPostNow.isPending ? '⚡ Posting…' : '⚡ Post now'}
          </button>
        )}
        {trip.source === 'centralized_request' && trip.status !== 'declined' && !trip.completed && !trip.do_not_post && (
          <button className="btn-secondary" onClick={() => { setUpdMsg(null); setUpdShow(true); if (!updQuoteRowId && liveQuotes.length) setUpdQuoteRowId(liveQuotes[0].id); }}
                  disabled={!liveQuotes.length}
                  title="Send an operator-corrected re-quote (a new Sentinel quote #) against the original held quote">
            ✎ Use updated Sentinel quote
          </button>
        )}
      </div>

      {postNowMsg && (
        <div className={`text-sm rounded-md px-3 py-2 ${postNowMsg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
          {postNowMsg.text}
        </div>
      )}

      {updMsg && (
        <div className={`text-sm rounded-md px-3 py-2 ${updMsg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
          {updMsg.text}
        </div>
      )}

      {updShow && (
        <div className="border rounded-md p-3 bg-teal-50 space-y-2">
          <div className="font-medium text-teal-900">Send an updated Sentinel quote for {trip.trip_id} to the customer</div>
          <div className="text-xs text-slate-600">
            For a quote you <strong>re-created in Sentinel</strong> (e.g. a crew-clean re-quote under a new Sentinel #).
            SQP sends that updated quote to the customer natively, then <strong>clears the original held quote</strong> below
            and marks the trip resolved. This action is <strong>real</strong>: the customer sees the updated quote.
          </div>
          <div className="text-[11px] text-slate-500">Original held quote to replace:</div>
          <select
            value={updQuoteRowId}
            onChange={e => setUpdQuoteRowId(e.target.value)}
            className="w-full border-slate-300 rounded-md text-sm px-2 py-1"
          >
            {liveQuotes.map(q => (
              <option key={q.id} value={q.id}>
                Q{q.sentinel_quote_id} · {q.primary_aircraft_tail} · ${q.final_price}{q.hold_reason ? ' · ⏸ held' : ''}{q.is_approved ? ' · already approved' : ''}
              </option>
            ))}
          </select>
          <input
            placeholder="Updated Sentinel quote # (e.g. 12042)"
            className="w-full border-slate-300 rounded-md text-sm px-2 py-1"
            value={updNewQid} onChange={e => setUpdNewQid(e.target.value)}
          />
          {updNewQid.trim() && !updQidValid && (
            <div className="text-[11px] text-rose-600">Enter the updated quote as a number (1–8 digits) — no “#”.</div>
          )}
          <div className="flex gap-2">
            <button className="btn-success" onClick={doSendUpdated} disabled={dSendUpdated.isPending || !updQuoteRowId || !updQidValid}>
              {dSendUpdated.isPending ? '✎ Sending…' : 'Confirm — send updated quote'}
            </button>
            <button className="btn-secondary" onClick={() => setUpdShow(false)} disabled={dSendUpdated.isPending}>Cancel</button>
          </div>
          {dSendUpdated.isError && <div className="text-sm text-rose-700">{dSendUpdated.error.message}</div>}
        </div>
      )}

      {useQ && (
        <div className="border rounded-md p-3 bg-sky-50 space-y-2">
          <div className="font-medium text-sky-900">Use a Sentinel quote for {trip.trip_id}</div>
          <div className="text-xs text-slate-600">
            For a quote you created directly in Sentinel (e.g. a new quote with an adjusted departure time to clear a
            crew-duty warning). SQP opens it, syncs the price, approves it, and Phase 4 posts it to Avinode.
            <strong> Send the quote in Sentinel first.</strong>
          </div>
          <div className="flex gap-2">
            <input
              placeholder="Sentinel quote # (e.g. 10773)"
              className="flex-1 border-slate-300 rounded-md text-sm px-2 py-1"
              value={useQid} onChange={e => setUseQid(e.target.value)}
            />
            <input
              placeholder="Tail (e.g. N711LZ)"
              className="w-36 border-slate-300 rounded-md text-sm px-2 py-1 uppercase"
              value={useTail} onChange={e => setUseTail(e.target.value)}
            />
            <input
              placeholder="Price (e.g. 32753)"
              type="number"
              className="w-40 border-slate-300 rounded-md text-sm px-2 py-1"
              value={usePrice} onChange={e => setUsePrice(e.target.value)}
            />
          </div>
          <div className="text-[11px] text-slate-500">Tail is required — Phase 4 needs it to post to the Avinode slot (a blank tail makes the post retry-loop).</div>
          <div className="flex gap-2">
            <button className="btn-success" onClick={doUseQuote} disabled={dUseQuote.isPending || !useQid.trim() || !usePrice || !useTail.trim()}>
              {dUseQuote.isPending ? 'Submitting…' : 'Use this quote'}
            </button>
            <button className="btn-secondary" onClick={() => setUseQ(false)}>Cancel</button>
          </div>
          {dUseQuote.isError && <div className="text-sm text-rose-700">{dUseQuote.error.message}</div>}
          {dUseQuote.isSuccess && <div className="text-sm text-emerald-700">Queued — the validator will reconcile this quote and Phase 4 will post it to Avinode.</div>}
        </div>
      )}

      {decline && (
        <div className="border rounded-md p-3 bg-rose-50 space-y-2">
          <div className="font-medium text-rose-900">Decline trip {trip.trip_id}?</div>
          {SOURCE_META[trip.source] && (
            <div className="text-xs text-slate-700 italic bg-white border border-slate-200 rounded p-2">
              {SOURCE_META[trip.source].declineNote}
            </div>
          )}
          {mruReasons.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Quick pick:</div>
              <div className="flex flex-wrap gap-1">
                {mruReasons.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setDeclineReason(r)}
                    className="text-xs px-2 py-1 rounded border border-rose-200 bg-white hover:bg-rose-100 text-rose-800"
                    title="Use this reason"
                  >
                    {r.length > 40 ? r.slice(0, 40) + '…' : r}
                  </button>
                ))}
              </div>
            </div>
          )}
          <input
            placeholder="Reason (e.g. 'crew duty', 'no availability')"
            className="w-full border-slate-300 rounded-md text-sm px-2 py-1"
            value={declineReason} onChange={e => setDeclineReason(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={declineForce} onChange={e => setDeclineForce(e.target.checked)} />
            --force (bypass substitution guard)
          </label>
          <div className="flex gap-2">
            <button className="btn-danger" onClick={doDecline} disabled={dDecline.isPending}>
              {dDecline.isPending ? 'Declining…' : 'Confirm decline'}
            </button>
            <button className="btn-secondary" onClick={() => setDecline(false)}>Cancel</button>
          </div>
          {dDecline.isError && <div className="text-sm text-rose-700">{dDecline.error.message}</div>}
        </div>
      )}

      {approve && (
        <div className="border rounded-md p-3 bg-emerald-50 space-y-2">
          <div className="font-medium text-emerald-900">Approve a quote for {trip.trip_id}</div>
          {isCr && (
            <div className="text-xs text-slate-600">
              For a CR quote, <strong>Approve delivers the selected quote to the customer</strong> in Sentinel
              (as-created), then resolves the trip and clears the other held quotes. This is <strong>real</strong>:
              the customer receives it. (~1–2 min while it sends.)
            </div>
          )}
          <select
            value={approveQid}
            onChange={e => setApproveQid(e.target.value)}
            className="w-full border-slate-300 rounded-md text-sm px-2 py-1"
          >
            {(isCr ? liveQuotes : quotes).map(q => (
              <option key={q.sentinel_quote_id} value={q.sentinel_quote_id}>
                Q{q.sentinel_quote_id} · {q.primary_aircraft_tail} · ${q.final_price}{q.hold_reason ? ' · ⏸ held' : ''}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button className="btn-success" onClick={doApprove} disabled={dApprove.isPending || !approveQid}>
              {dApprove.isPending ? (isCr ? 'Approving & sending…' : 'Approving…') : (isCr ? 'Approve & send' : 'Confirm approve')}
            </button>
            <button className="btn-secondary" onClick={() => setApprove(false)} disabled={dApprove.isPending}>Cancel</button>
          </div>
          {dApprove.isError && <div className="text-sm text-rose-700">{dApprove.error.message}</div>}
        </div>
      )}

      {reset && (
        <div className="border rounded-md p-3 bg-slate-100 space-y-2">
          <div className="font-medium">Reset trip {trip.trip_id} for re-extraction?</div>
          <div className="text-xs text-slate-600">
            {trip.source === 'avinode'
              ? 'Clears all 4 Phase 1 dedup sources. Only re-extracts if Avinode still shows trip as Unanswered.'
              : `Re-queues the trip for Phase 2 (listener picks it up within 5s). No Avinode interaction — source is ${trip.source}.`}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={resetForce} onChange={e => setResetForce(e.target.checked)} />
            --force (bypass "already posted to Avinode" safety check)
          </label>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={doReset} disabled={dReset.isPending}>
              {dReset.isPending ? 'Resetting…' : 'Confirm reset'}
            </button>
            <button className="btn-secondary" onClick={() => setReset(false)}>Cancel</button>
          </div>
          {dReset.isError && <div className="text-sm text-rose-700">{dReset.error.message}</div>}
        </div>
      )}
    </div>
  );
}

export default function TripDetail() {
  const { tripId } = useParams();
  // S98: ?operator= disambiguates trip_id collisions (same Avinode trip exists
  // for multiple operators — H557YZ raber/sprintbach). Without it the server
  // returns the most recently created row, i.e. possibly the WRONG operator.
  const [searchParams] = useSearchParams();
  const operator = searchParams.get('operator') || null;
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useTripDetail(tripId, operator);

  function refetch() {
    qc.invalidateQueries({ queryKey: ['trip', tripId] });
    qc.invalidateQueries({ queryKey: ['operators'] });
  }

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (isError)   return <div className="text-rose-700">Error: {error.message}</div>;

  const { trip, sqp_state, avinode_state, quotes, trs_verdicts, phase4_log, audit_log, badge, do_not_post_reason } = data;
  const legs = trip.legs || [];
  const aircraft = trip.aircraft || [];
  const tailSubs = trip.tail_substitutions;

  // TRS effective-verdict: a verdict a reviewer Declined-corrected to SEND (server attaches
  // .disposition, mirroring /trs). Drives the green "Go — send quote" banner in the panel below.
  const correctedToSend = (trs_verdicts || []).find(
    v => v.disposition?.pill === 'Declined' && v.disposition?.corrected_verdict === 'SEND'
  );
  const sendBanner = correctedToSend
    ? { qid: correctedToSend.qid, reviewer: correctedToSend.disposition.reviewer, note: correctedToSend.disposition.note }
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
          <div>
            <Link to="/trips" className="text-xs text-sentinel-navy hover:underline">← All trips</Link>
            <h1 className="text-2xl font-bold font-mono">{trip.trip_id}</h1>
            <div className="text-sm text-slate-600 capitalize">{trip.operator_id} · {trip.source}</div>
          </div>
          <div className="text-right space-y-1">
            <StatusBadge badge={badge} />
            <div className="text-xs text-slate-500">{trip.customer_name || '—'}</div>
            {trip.customer_email && (
              <div className="font-mono text-[11px] text-slate-400 break-all">{trip.customer_email}</div>
            )}
            {(trip.broker_name || trip.broker_id) && (
              <div className="text-xs text-slate-500">
                <span className="opacity-70">Broker:</span> {trip.broker_name || trip.broker_id}
                {trip.broker_email && (
                  <div className="font-mono text-[11px] text-slate-400 break-all">{trip.broker_email}</div>
                )}
              </div>
            )}
          </div>
        </div>
        {do_not_post_reason && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            <strong>Why Do Not Post:</strong> {do_not_post_reason}
          </div>
        )}
      </div>

      {/* Why this trip ended where it did — Phase 2 decision + operator reason.
          S99: read from trip.* — these fields were pruned from sqp_state so the
          raw state column no longer duplicates this panel. */}
      <Phase2DecisionPanel
        decision={trip.phase2_decision}
        note={trip.phase2_decision_note}
        operatorReason={trip.operator_decline_reason}
        crPhase={trip.cr_phase}
        crReason={trip.source === 'centralized_request' ? trip.hold_reason : null}
        sendBanner={sendBanner}
      />

      {/* Non-Avinode sources (webhook/telegram/email): surface broker_id, request_id,
          and note that decline does NOT touch Avinode. */}
      <SourceInfoPanel trip={trip} />

      {/* SQP vs Avinode state — only meaningful for Avinode-source trips.
          Webhook/Telegram/Email trips never touch Avinode, so the right column
          is always empty for them. Hide entirely and show the SQP state inline. */}
      {trip.source === 'avinode' ? (
        <Section title="SQP intent vs Avinode-confirmed state">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StateColumn label="SQP says" state={sqp_state} />
            <StateColumn label="Avinode confirms (from phase4_post_log)" state={avinode_state} />
          </div>
          <div className="mt-3 text-xs text-slate-500 italic">
            The avinode_response_* fields are SQP's intent at the time of `/decline` or `/approve` — not confirmation that Avinode received it. The right column is the source of truth.
          </div>
        </Section>
      ) : (
        <Section title="SQP state">
          <StateColumn label="SQP says" state={sqp_state} />
        </Section>
      )}

      {/* Aircraft + Legs */}
      <Section title="Request">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs uppercase text-slate-500 mb-1">Aircraft requested</h4>
            <ul className="text-sm space-y-1">
              {aircraft.map((a, i) => (
                <li key={i} className="font-mono text-xs">
                  {a.tailNumber || a.tail} · {a.type || '?'}
                  {a.sellerPrice > 0 && (
                    <span className="text-sky-700" title="Requested price as shown on the Avinode request — market reference for pricing decisions">
                      {' · '}Requested {fmtAvinodePrice(a.sellerPrice, a.currency)}
                    </span>
                  )}
                  {tailSubs && tailSubs[a.tailNumber] && (
                    <span className="badge badge-amber ml-2">→ {tailSubs[a.tailNumber]} (sub)</span>
                  )}
                </li>
              ))}
              {!aircraft.length && <li className="text-slate-500 text-xs">(none)</li>}
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase text-slate-500 mb-1">Legs</h4>
            <ul className="text-sm space-y-1">
              {legs.map((l, i) => (
                <li key={i} className="font-mono text-xs">
                  {l.fromAirport}→{l.toAirport} · {l.date} {l.time || ''} · {l.pax || '?'} pax · {l.nm || '?'} nm
                </li>
              ))}
              {!legs.length && <li className="text-slate-500 text-xs">(none)</li>}
            </ul>
          </div>
        </div>
      </Section>

      {/* Quotes */}
      <Section title={`Quotes (${quotes.length})`}>
        {quotes.length === 0 ? (
          <div className="text-sm text-slate-500">No quotes generated.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-1 pr-3">Q</th>
                  <th className="py-1 pr-3">Tail</th>
                  <th className="py-1 pr-3">Price</th>
                  <th className="py-1 pr-3" title="Champion (floor-check) PE verdict">PE chmp</th>
                  <th className="py-1 pr-3" title="Challenger (contribution) PE verdict">PE chal</th>
                  <th className="py-1 pr-3" title="PE recommended price minus sent price (challenger when running, else champion)">Δ</th>
                  <th className="py-1 pr-3">Booked</th>
                  <th className="py-1 pr-3">Sent</th>
                  <th className="py-1 pr-3">Posted</th>
                  <th className="py-1 pr-3">Hold</th>
                  <th className="py-1">Created</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id} className="border-b last:border-0">
                    <td className="py-1 pr-3"><QuoteIdLink id={q.sentinel_quote_id} url={q.sentinel_url} /></td>
                    <td className="py-1 pr-3">{q.primary_aircraft_tail || '—'}</td>
                    <td className="py-1 pr-3">${q.final_price?.toLocaleString() || '—'}</td>
                    <td className="py-1 pr-3"><PeCell d={q.pe?.champion} /></td>
                    <td className="py-1 pr-3"><PeCell d={q.pe?.challenger} /></td>
                    <td className="py-1 pr-3"><DeltaCell delta={q.pe?.delta} /></td>
                    <td className="py-1 pr-3"><BookedCell booked={q.booked} /></td>
                    <td className="py-1 pr-3">{q.is_approved ? '✓' : '—'}</td>
                    <td className="py-1 pr-3">{q.is_sent_to_sentinel ? '✓' : '—'}</td>
                    <td className="py-1 pr-3">
                      {q.cleared_at ? <span className="text-slate-500">cleared</span> :
                       q.hold_reason ? <span className="text-amber-700" title={q.hold_reason}>⏸ held</span> :
                       '—'}
                    </td>
                    <td className="py-1">{q.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Trip Request Solver (TRS) — the crew/ops solver's shadow verdict(s) for this trip's held
          crew-warned quotes: SEND/HOLD + reasoning + levers tried + expandable candidate trace.
          Only renders when a verdict was logged (no noise on non-crew trips). Same card as /trs. */}
      {Array.isArray(trs_verdicts) && trs_verdicts.length > 0 && (
        <Section title={`Trip Request Solver (TRS) — ${trs_verdicts.length} verdict${trs_verdicts.length === 1 ? '' : 's'}`}>
          <div className="text-xs text-slate-500 mb-2">
            Shadow — these verdicts are recorded, not acted on. Latest verdict per quote; the solver
            re-slots positioning legs to clear the crew RED chip (FAA Part 135 flight-time / rest).
          </div>
          <div className="space-y-2">
            {trs_verdicts.map(v => <VerdictCard key={v.id} v={v} onReviewed={refetch} />)}
          </div>
        </Section>
      )}

      {/* PE shadow assessment — champion vs challenger reasoning, side by side */}
      {quotes.some(q => q.pe && (q.pe.champion || q.pe.challenger)) && (
        <Section title="PE shadow assessment">
          <div className="text-xs text-slate-500 mb-2">
            Observation only — these verdicts are recorded but not enforced. Challenger uses
            contribution framing (does a discount buy enough win-probability to justify the margin given up?).
          </div>
          {quotes.map(q => <PeReasonRow key={q.id} q={q} />)}
        </Section>
      )}

      {/* Phase 4 log */}
      <Section title={`Phase 4 log (${phase4_log.length})`}>
        {phase4_log.length === 0 ? (
          <div className="text-sm text-slate-500">No Phase 4 attempts.</div>
        ) : (
          <ul className="text-xs font-mono space-y-1">
            {phase4_log.map(r => (
              <li key={r.id} className={r.success ? 'text-emerald-700' : 'text-rose-700'}>
                {r.posted_at} {r.success ? '✓' : '✗'} {r.action}
                {r.tail_number && ` tail=${r.tail_number}`}
                {r.sentinel_quote_id && ` Q${r.sentinel_quote_id}`}
                {r.price && ` $${r.price}`}
                {r.error && <div className="text-xs text-rose-600 ml-4">{r.error}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Audit log */}
      <Section title={`Audit log (${audit_log.length})`}>
        {audit_log.length === 0 ? (
          <div className="text-sm text-slate-500">No related audit events.</div>
        ) : (
          <ul className="text-xs font-mono space-y-1">
            {audit_log.map(a => (
              <li key={a.id}>
                <span className="text-slate-500">{a.created_at}</span>{' '}
                <span className="font-semibold">{a.event_type}</span>{' '}
                <span className="text-slate-500">by {a.actor || '?'}</span>
                {a.details && <div className="text-slate-500 ml-4 break-all">{a.details}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Actions */}
      <ActionBar trip={trip} quotes={quotes} onAction={refetch} />
    </div>
  );
}
